import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { io } from 'socket.io-client';

const SharedDataContext = createContext();

export const useSharedData = () => useContext(SharedDataContext);

export const SharedDataProvider = ({ children }) => {
    // Socket state
    const [socket, setSocket] = useState(null);
    const [socketConnected, setSocketConnected] = useState(false);
    const [socketError, setSocketError] = useState(null);
    const [wasConnectedBefore, setWasConnectedBefore] = useState(false);
    const reconnectTimerRef = useRef(null);

    // Other shared states
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [detailViewType, setDetailViewType] = useState('CallDetail');
    const [lastMessage, setLastMessage] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTenant, setSelectedTenant] = useState(localStorage.getItem('selectedTenant') || '');

    // Get auth from Auth0
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();

    // Socket event listeners registry
    const [eventListeners, setEventListeners] = useState({});

    // Initialize socket connection
    useEffect(() => {
        const connectSocket = async () => {
            try {
                if (isAuthenticated) {
                    const token = await getAccessTokenSilently();

                    const isLocalhost = window.location.hostname === 'localhost';
                    const socketUrl = isLocalhost ? 'http://localhost:3001' : '/';

                    console.log("Connecting socket using path " + socketUrl);

                    // Configure socket with reconnection options
                    const newSocket = io(socketUrl, {
                        query: { token, 'x-tenant': selectedTenant },
                        timeout: 5000,
                        reconnectionAttempts: 10,         // Try 10 times for initial connection
                        reconnectionDelay: 1000,          // Start with 1s delay
                        reconnectionDelayMax: 5000,       // Max 5s delay
                        randomizationFactor: 0.5,         // Add randomization to prevent thundering herd
                        transports: ['websocket', 'polling']
                    });

                    if (newSocket) {
                        setSocket(newSocket);

                        // Set up default event listeners
                        newSocket.on('connect', () => {
                            console.log('Socket connected');
                            setSocketConnected(true);
                            setSocketError(null);
                            setWasConnectedBefore(true);

                            // Clear any reconnect timers on successful connection
                            if (reconnectTimerRef.current) {
                                clearInterval(reconnectTimerRef.current);
                                reconnectTimerRef.current = null;
                            }
                        });

                        newSocket.on('disconnect', (reason) => {
                            console.log(`Socket disconnected: ${reason}`);
                            setSocketConnected(false);

                            // If this wasn't a manual disconnect and we were connected before,
                            // set up a reconnection timer
                            if (reason !== 'io client disconnect' && wasConnectedBefore) {
                                console.log("Starting manual reconnection timer");
                                startReconnectionTimer(newSocket);
                            }
                        });

                        newSocket.on('connect_error', (err) => {
                            console.error('Socket connection error:', err);
                            setSocketError(err.message);
                            setSocketConnected(false);

                            // If we were connected before, try to reconnect
                            if (wasConnectedBefore) {
                                startReconnectionTimer(newSocket);
                            }
                        });

                        // Other event listeners...
                    }
                }
            } catch (e) {
                console.error('Failed to connect websocket:', e);
                setSocketError(e.message);
                setSocketConnected(false);

                // If we were connected before, try to reconnect
                if (wasConnectedBefore && socket) {
                    startReconnectionTimer(socket);
                }
            }
        };

        connectSocket();

        // Clean up on unmount
        return () => {
            if (socket) {
                console.log('Disconnecting socket...');
                socket.disconnect();
                setSocketConnected(false);
            }

            // Clear any reconnection timers
            if (reconnectTimerRef.current) {
                clearInterval(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };
    }, [isAuthenticated, getAccessTokenSilently, selectedTenant]);

    const startReconnectionTimer = (socketInstance) => {
        // If a timer is already running, don't start another
        if (reconnectTimerRef.current) return;

        // Try to reconnect every 10 seconds indefinitely
        reconnectTimerRef.current = setInterval(() => {
            console.log("Attempting to reconnect socket...");
            if (socketInstance && !socketInstance.connected) {
                socketInstance.connect();
            } else {
                // If already connected or socket is gone, clear the timer
                clearInterval(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        }, 10000);
    };



    // Apply registered event listeners whenever socket or event listeners change
    useEffect(() => {
        if (socket && socketConnected) {
            // First remove any existing listeners to prevent duplicates
            Object.keys(eventListeners).forEach(event => {
                const listeners = eventListeners[event] || [];
                listeners.forEach(({ callback, listenerId }) => {
                    socket.off(event, callback);
                });
            });

            // Then add all current listeners
            Object.keys(eventListeners).forEach(event => {
                const listeners = eventListeners[event] || [];
                listeners.forEach(({ callback, listenerId }) => {
                    socket.on(event, callback);
                });
            });
        }
    }, [socket, socketConnected, eventListeners]);

    // Function to add an event listener
    const addEventListener = useCallback((event, callback) => {
        const listenerId = `${event}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        setEventListeners(prev => {
            const eventArray = prev[event] || [];
            return {
                ...prev,
                [event]: [...eventArray, { callback, listenerId }]
            };
        });

        // If the socket is already connected, add the listener immediately
        if (socket && socketConnected) {
            socket.on(event, callback);
        }

        // Return a function to remove this specific listener
        return () => {
            setEventListeners(prev => {
                const updatedListeners = { ...prev };
                if (updatedListeners[event]) {
                    updatedListeners[event] = updatedListeners[event].filter(
                        item => item.listenerId !== listenerId
                    );
                }
                return updatedListeners;
            });

            // Also remove from the actual socket if it exists
            if (socket && socketConnected) {
                socket.off(event, callback);
            }
        };
    }, [socket, socketConnected]);

    // Function to emit an event
    const emitEvent = useCallback((event, ...args) => {
        if (socket && socketConnected) {
            socket.emit(event, ...args);
            return true;
        } else {
            console.warn('Socket not connected, cannot emit event:', event);
            return false;
        }
    }, [socket, socketConnected]);

    // Function to disconnect socket
    const disconnect = useCallback(() => {
        if (socket) {
            socket.disconnect();
        }
    }, [socket]);


    const reconnect = useCallback(async () => {
        if (socket) {
            // Clear any existing timers
            if (reconnectTimerRef.current) {
                clearInterval(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            socket.connect();
        } else {
            // If socket doesn't exist, we need to create a new one
            // This will trigger the useEffect above
            setSocket(null);
        }
    }, [socket]);


    const useSocketEvent = (eventName, callback) => {
        // Store reference to avoid issues with stale closures
        const callbackRef = useRef(callback);
        callbackRef.current = callback;

        useEffect(() => {
            if (!socket || !socketConnected) return;

            const handler = (...args) => callbackRef.current(...args);
            socket.on(eventName, handler);

            return () => {
                socket.off(eventName, handler);
            };
        }, [eventName, socket, socketConnected]);
    };

    // Socket API exposed to components
    const socketApi = {
        connected: socketConnected,
        error: socketError,

        // Simple emit function
        emit: (event, data) => {
            if (socket && socketConnected) {
                socket.emit(event, data);
                return true;
            }
            return false;
        },

        disconnect,
        reconnect
    };

    return (
        <SharedDataContext.Provider
            value={{
                socket: socketApi,
                useSocketEvent,
                searchQuery,
                setSearchQuery,
                dateRange,
                setDateRange,
                showNotification,
                setShowNotification,
                lastMessage,
                setLastMessage,
                selectedRecord,
                setSelectedRecord,
                detailViewType,
                setDetailViewType,
                selectedTenant,
                setSelectedTenant,
            }}
        >
            {children}
        </SharedDataContext.Provider>
    );
};

export default SharedDataContext;
